// @ts-strict-ignore
import omit from 'lodash/omit';
import * as React from 'react';

import {getVirtualSlotChildren, JSX_CHILDREN_PROP} from 'common/util/virtual-slots';

import type {ModifierKeys} from './keyboard';
import {CROSS_PROJECT_TRACKING_PREFIXES, getNow} from './timing';

export const ENTER_KEY_CODE = 13;
export const ESCAPE_KEY_CODE = 27;
export const SHIFT_KEY_CODE = 16;
export const SPACE_KEY_CODE = 32;
export const TAB_KEY_CODE = 9;
export const NAVIGATION_KEY_CODES = {
  TAB: TAB_KEY_CODE,
  ENTER: ENTER_KEY_CODE,
  UP_ARROW: 38,
  DOWN_ARROW: 40,
};

export const KEY_VALUES = {
  ARROW_DOWN: `ArrowDown`,
  ARROW_UP: `ArrowUp`,
  ARROW_LEFT: `ArrowLeft`,
  ARROW_RIGHT: `ArrowRight`,
  BACKSPACE: `Backspace`,
  ENTER: `Enter`,
  ESCAPE: `Escape`,
  TAB: `Tab`,
  CMD: `Meta`,
  CTRL: `Ctrl`,
  BACKSLASH: `Backslash`,
};

export const MOUSE_BUTTONS = {
  LEFT: 0,
  MIDDLE: 1,
  RIGHT: 2,
};

/**
 * Checks for presence of a slotted child element
 */
export function hasSlot(el, slotName) {
  // If component has enabled useVirtualSlot, slot children can only be reliably found in el.$children
  if (el[JSX_CHILDREN_PROP]) {
    return getVirtualSlotChildren(el[JSX_CHILDREN_PROP], slotName).length > 0;
  }
  return !!el.querySelector(`[slot="${slotName}"]`);
}

export function eventPathContainsSelectors(ev: React.MouseEvent | MouseEvent | Event, selectors: string[]) {
  // events from shadow DOM will be retargeted to appear as if they come from component
  const event = (ev as React.MouseEvent)?.nativeEvent ?? (ev as MouseEvent);
  let path = (event.composedPath?.() as HTMLElement[]) ?? [];
  if (path.length === 0 && event.target instanceof HTMLElement) {
    path = getAncestorElements(event.target) as HTMLElement[];
  }

  const matchSelector = selectors.join(`,`);
  const containsSelectors = Boolean(matchSelector) && path.some((el) => el.matches?.call(el, matchSelector));

  return {path, containsSelectors};
}

export function eventWasOutside(ev, subjectEl, preventCloseSelectors = []) {
  const {path, containsSelectors} = eventPathContainsSelectors(ev, preventCloseSelectors || []);
  return !path.includes(subjectEl) && !containsSelectors;
}

// browsers like MicrosoftEdge will fire both newer 'animationend' and legacy 'MSAnimationEnd'
// To preserve backwards compatibility we have a helper that only calls the callback once
// Open in new tab key modifier is OS specific. Keep key check here to isolate OS specific divergence
export function shouldOpenInNewTab(ev: MouseEvent | KeyboardEvent | CustomEvent<{modifierKeys: ModifierKeys}>) {
  if (ev instanceof MouseEvent || ev instanceof KeyboardEvent) {
    return ev.ctrlKey || ev.metaKey;
  }

  if (ev?.detail?.modifierKeys) {
    const modifiers = ev.detail.modifierKeys;

    return modifiers.Ctrl || modifiers.Cmd;
  }

  return false;
}

/**
 * Returns the closest parent element matching the given selector,
 * or null if no match is found or the selector is falsy.
 */
/**
 * Scroll to vertically center an element in the window
 */
/**
 * Scroll element to align with the bottom of the viewport if currently cut-off.
 */
export function scrollIntoViewIfNeeded(element: Element, scrollParent: Element | Window = document.documentElement) {
  const parentReference =
    scrollParent === window || scrollParent === document.body ? document.documentElement : (scrollParent as Element);
  // don't move this out of the function otherwise unit tests will break because of the `document` reference
  const SMOOTH_SCROLL_SUPPORTED = `scrollBehavior` in document.documentElement.style;
  const {bottom, top} = element.getBoundingClientRect();
  const {bottom: parentBottom} = parentReference.getBoundingClientRect();
  /*
  yDiff is the distance from the bottom of the element to the bottom of the viewport.
  A positive value means the element's bottom is past the bottom of the viewport.
  Negative means the element's bottom is somewhere above the bottom of the viewport.
  Any value less than -1 * (viewport height) means the element's bottom is past the top of the viewport.
  */
  const yDiff = bottom - parentBottom;
  const elementIsFullyInViewAlready = yDiff <= 0 && yDiff >= -parentReference.clientHeight && top >= 0;

  // Don't unecessarily scroll if the element is already in view.
  if (!elementIsFullyInViewAlready) {
    if (SMOOTH_SCROLL_SUPPORTED) {
      // element.scrollIntoView({behavior: `scroll`, block: `end`, inline: `nearest`}) is buggy if the element is just appearing,
      // so we use a custom scrollTo() instead
      scrollParent.scrollTo({
        behavior: `smooth`,
        top: parentReference.scrollTop + yDiff,
      });
    } else {
      element.scrollIntoView(false);
    }
  }
}

/**
 * Smooth scrolls an scrollable element to an x, y position
 */
export function propagateEvent(el, ev) {
  ev.stopPropagation();
  el.dispatchEvent(new CustomEvent(ev.type, ev));
}

export function preventTracking(ev) {
  /**
  Sometimes we need to prevent duplicated track events. We'll key off the
   cancelable field to determine if we propagate these events or not.
   */
  if (ev.cancelable) {
    ev.stopPropagation();
  }
}

/**
 * Emit a `trackinfra` event in a format understood by ReportTracker.
 *
 * @param {HTMLElement} el element to dispatch the event from
 * @param {any} options
 * @param {any} props properties to track
 * @param {string?} eventPrefix optionally tag the event name with a [prefix]
 */
export function emitInfraTrackEvent(
  el,
  {
    eventName = ``,
    eventPrefix = ``,
    props = {},
    cancelable = false,
  }: {eventName?: string; eventPrefix?: string; props?: Record<string, any>; cancelable?: boolean} = {},
) {
  const infraProps: Record<string, any> = {...props, now: getNow()};
  emitTrackEvent(el, {eventName, eventPrefix, props: infraProps, cancelable}, `trackinfra`);
  // perf events are helpful to have in proj 3 as well so we can understand a full user session
  if (
    CROSS_PROJECT_TRACKING_PREFIXES.includes(eventPrefix) ||
    CROSS_PROJECT_TRACKING_PREFIXES.some(
      (prefix) => eventName.startsWith(prefix) || eventName.startsWith(`[${prefix}]`),
    )
  ) {
    // for P3 tracking, don't use alternate distinct_id (like project ID)
    const p3Props = omit(props, `distinct_id`);
    emitTrackEvent(el, {eventName, eventPrefix, props: p3Props, cancelable}, `track`);
  }
}

/**
 * Emit a `track` event in a format understood by ReportTracker.
 *
 * @param {HTMLElement} el element to dispatch the event from
 * @param {any} options
 * @param {any} props properties to track
 * @param {string?} eventPrefix optionally tag the event name with a [prefix]
 */
export function emitTrackEvent(
  el,
  {eventName = ``, eventPrefix = ``, props = {}, cancelable = false} = {},
  trackEvent = `track`,
) {
  const maybeEventPrefix = eventPrefix ? `[${eventPrefix}] ` : null;
  el.dispatchEvent(
    new CustomEvent(trackEvent, {
      bubbles: true,
      composed: true,
      cancelable,
      detail: {
        props,
        eventPrefix: maybeEventPrefix,
        eventName: maybeEventPrefix ? `${maybeEventPrefix}${eventName}` : eventName,
      },
    }),
  );
}

function toAttributeString(val) {
  if (typeof val === `string`) {
    return val;
  }
  return JSON.stringify(val);
}

/**
 * A simple helper for creating nested dom elements with attributes
 * @param {string} name
 * @param {{[attr: string]: any}} attrs
 * @param {(string|HTMLElement)[]} children
 * @returns {HTMLElement}
 */
export function createElem(name, attrs = {}, children: string | Array<string | Element> = []) {
  const elem = document.createElement(name);

  for (const [attr, val] of Object.entries(attrs || {})) {
    elem.setAttribute(attr, toAttributeString(val));
  }

  if (!Array.isArray(children)) {
    children = [children];
  }

  for (const child of children) {
    if (typeof child === `string`) {
      elem.appendChild(document.createTextNode(child));
    } else if (child instanceof Element) {
      elem.appendChild(child);
    } else {
      throw new Error(`${String(child)} cannot be appended as a child. Need string or Element`);
    }
  }

  return elem;
}

/**
 * @callback rafCallback
 * @returns {boolean} True when callback complete its work successfully, false if work could not be completed
 */

/**
 * Takes a predicate function and max retry count. The predicate function will be called until it returns true or
 * the max retry is reached. The promise will resolve if the predicate successfully returns true before max retries
 *
 * @param {rafCallback} rafCallback
 * @param {number} maxRetry
 */
export async function rafRetry(rafCallback: () => boolean, {maxRetry = 2, raiseError = false} = {}) {
  return new Promise<void>((resolve, reject) => {
    let tries = 0;
    const executeRaf = (): void => {
      if (tries < maxRetry) {
        tries++;
        if (!rafCallback()) {
          window.requestAnimationFrame(executeRaf);
        } else {
          resolve();
        }
      } else if (raiseError) {
        reject(new Error(`Max retries of ${maxRetry} reached`));
      } else {
        resolve();
      }
    };
    window.requestAnimationFrame(executeRaf);
  });
}

/**
 * A simple helper for asynchronous sleep
 * @param {number} delay
 * @param {AbortSignal} signal
 * @returns {Promise<void>}
 */
export function sleep(delay: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    let abort;

    const timeout = setTimeout(() => {
      resolve();
      signal?.removeEventListener(`abort`, abort);
    }, delay);

    abort = () => {
      clearTimeout(timeout);
      reject(new Error(`sleep aborted`));
    };

    signal?.addEventListener(`abort`, abort);
  });
}

/**
 * Helper to check for boolean attributes.
 * @param {HTMLElement} el
 * @param {string} attrName
 * @returns {boolean}
 */
export function isAttributeEnabled(el: HTMLElement, attrName: string) {
  return (
    el.hasAttribute(attrName) &&
    [``, `true`, attrName.toLowerCase()].includes((el.getAttribute(attrName) ?? ``).toLowerCase())
  );
}

/**
 * Calculates total dom height of a node including margin, padding, border, and height
 * @param {HTMLElement} node
 * @returns {number}
 */
export function getElemHeight(node: HTMLElement): number {
  const list = [
    `margin-top`,
    `margin-bottom`,
    `border-top`,
    `border-bottom`,
    `padding-top`,
    `padding-bottom`,
    `height`,
  ];

  const style = window.getComputedStyle(node);
  return list.map((k) => parseInt(style.getPropertyValue(k), 10)).reduce((prev, cur) => prev + cur);
}

export function prefersDarkColorScheme(): boolean {
  return window.matchMedia(`(prefers-color-scheme: dark)`).matches;
}

/**
 * Check if an element would show ellipsis because of overflowing content
 */
export function isEllipsisActive(el: HTMLElement) {
  return (
    // Check for ellipsis for single line text (using 'text-overflow: ellipsis'). Based on
    // https://stackoverflow.com/a/10017343
    el.offsetWidth < el.scrollWidth ||
    // Check for ellipsis for multi-line text with ellipsis (using -webkit-line-clamp)
    el.offsetHeight < el.scrollHeight
  );
}

/**
 * This gets n custom element parents. This is intended to provide some context on
 * where in the UI the given element is located.
 *
 * @param element HTMLElement or CustomElement to start traversal from
 * @param num The number of custom element parents to return
 */
export function getCustomElementParents(element: Element, num = 3): string[] {
  const parents: string[] = [];
  let elm = element.parentNode as Element | ShadowRoot;

  while (elm && typeof elm === `object` && parents.length < num) {
    if (`tagName` in elm && elm.tagName.includes(`-`)) {
      parents.unshift(elm.tagName.toLowerCase());
    }
    elm = `host` in elm ? elm.host : (elm.parentNode as Element | ShadowRoot);
  }
  return parents;
}

/**
 * This checks if an element is contained in another (pierces shadow dom)
 *
 * @param parentElement HTMLElement or CustomElement that is the suspected parent
 * @param element HTMLElement or CustomElement that is the suspected child
 * @returns {boolean}
 */
export function shadowContains(parentElement: Element, element: Element): boolean {
  let elm = element.parentNode as Element | ShadowRoot;

  while (elm && typeof elm === `object`) {
    if (parentElement === elm) {
      return true;
    }
    elm = `host` in elm ? elm.host : (elm.parentNode as Element | ShadowRoot);
  }
  return false;
}

function getParentElement(element: Element): Element | null {
  const parentNode = element.assignedSlot?.parentNode ?? element.parentNode;
  // Document(nodeType=9) and DocumentFragment(nodeType=11) has no parent
  if (!parentNode) {
    return null;
  }
  // traversing to a ShadowRoot node
  if (parentNode.nodeType === 11) {
    return (parentNode as ShadowRoot).host;
  }
  return parentNode as Element;
}

export function getAncestorElements(element: Element): Element[] {
  const ancestors: Element[] = [];
  let parent = getParentElement(element);
  while (parent) {
    ancestors.push(parent);
    parent = getParentElement(parent);
  }
  return ancestors;
}

/**
 * element.offsetParent is not consistent for all browsers
 * this util method ensures that we get the correct offsetParent in firefox,safari,chrome
 * This util will always default to document.body if no offsetParent is found
 *
 * MDN: https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
 * modified from https://gist.github.com/Rycochet/a797f78bc93fa274d32550ad99644101
 */
export function getOffsetParent(element: HTMLElement): Element {
  let parent = getParentElement(element);
  if (!parent) {
    return document.body;
  }
  const html = document.documentElement;
  let found: Element | null = null;
  let style = window.getComputedStyle(element);

  if (style.position !== `fixed`) {
    for (; parent; parent = getParentElement(parent)) {
      style = window.getComputedStyle(parent);
      if (style.display === `none`) {
        found = null;
        break;
      }
      if (!found && (parent === html || style.position !== `static`)) {
        found = parent;
        break;
      }
    }
  }

  return found || document.body;
}

// This function returns the closest ancestor element that is scrollable
// taken from https://stackoverflow.com/questions/35939886/find-first-scrollable-parent/49186677#49186677
export function getScrollParent(node: HTMLElement): HTMLElement {
  const regex = /(auto|scroll)/;
  const parents = (_node, ps) => {
    if (_node.parentNode === null) {
      return ps;
    }
    return parents(_node.parentNode, ps.concat([_node]));
  };

  const style = (_node, prop) => getComputedStyle(_node, null).getPropertyValue(prop);
  const overflow = (_node) => style(_node, `overflow`) + style(_node, `overflow-y`) + style(_node, `overflow-x`);
  const scroll = (_node) => regex.test(overflow(_node));

  /* eslint-disable consistent-return */
  const scrollParent = (_node) => {
    if (!(_node instanceof HTMLElement || _node instanceof SVGElement)) {
      return;
    }

    const ps = parents(_node.parentNode, []);

    for (let i = 0; i < ps.length; i += 1) {
      if (scroll(ps[i])) {
        return ps[i];
      }
    }

    return document.scrollingElement || document.documentElement;
  };

  return scrollParent(node);
  /* eslint-enable consistent-return */
}

export function isElementInViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}
